// src/services/menuService.js
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase-config";

export class MenuService {
  constructor() {
    this.menuItemsCollection = "menu_items";
  }

  // Add restaurant ID to all menu operations
  getMenuItemsQuery(restaurantId, additionalFilters = []) {
    const constraints = [
      where("restaurantId", "==", restaurantId),
      orderBy("order", "asc"),
      ...additionalFilters,
    ];

    return query(collection(db, this.menuItemsCollection), ...constraints);
  }

  // Get menu items for a specific restaurant
  async getMenuItems(restaurantId, filters = {}) {
    try {
      const additionalFilters = [];

      if (filters.category) {
        additionalFilters.push(where("category", "==", filters.category));
      }

      if (filters.inStock !== undefined) {
        additionalFilters.push(where("inStock", "==", filters.inStock));
      }

      const q = this.getMenuItemsQuery(restaurantId, additionalFilters);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting menu items:", error);
      throw error;
    }
  }

  // Listen to menu items changes
  onMenuItemsChange(restaurantId, callback, filters = {}) {
    try {
      const additionalFilters = [];

      if (filters.category) {
        additionalFilters.push(where("category", "==", filters.category));
      }

      if (filters.inStock !== undefined) {
        additionalFilters.push(where("inStock", "==", filters.inStock));
      }

      const q = this.getMenuItemsQuery(restaurantId, additionalFilters);

      return onSnapshot(q, (snapshot) => {
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(items);
      });
    } catch (error) {
      console.error("Error listening to menu items:", error);
      throw error;
    }
  }

  // Alias method for PublicMenu compatibility
  subscribeToMenuItems(restaurantId, onSuccess, onError, filters = {}) {
    try {
      return this.onMenuItemsChange(restaurantId, onSuccess, filters);
    } catch (error) {
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }

  // Add menu item
  async addMenuItem(restaurantId, itemData) {
    try {
      const item = {
        ...itemData,
        restaurantId,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const docRef = await addDoc(
        collection(db, this.menuItemsCollection),
        item
      );
      return {
        id: docRef.id,
        ...item,
      };
    } catch (error) {
      console.error("Error adding menu item:", error);
      throw error;
    }
  }

  // Update menu item
  async updateMenuItem(itemId, updates) {
    try {
      const itemRef = doc(db, this.menuItemsCollection, itemId);
      await updateDoc(itemRef, {
        ...updates,
        updatedAt: new Date(),
      });
      return true;
    } catch (error) {
      console.error("Error updating menu item:", error);
      throw error;
    }
  }

  // Delete menu item
  async deleteMenuItem(itemId) {
    try {
      const itemRef = doc(db, this.menuItemsCollection, itemId);
      await deleteDoc(itemRef);
      return true;
    } catch (error) {
      console.error("Error deleting menu item:", error);
      throw error;
    }
  }

  // Bulk import menu items (for document processing)
  async bulkImportMenuItems(restaurantId, items) {
    try {
      const results = [];

      for (const item of items) {
        const menuItem = await this.addMenuItem(restaurantId, {
          ...item,
          source: "bulk_import",
        });
        results.push(menuItem);
      }

      return {
        imported: results.length,
        items: results,
      };
    } catch (error) {
      console.error("Error bulk importing menu items:", error);
      throw error;
    }
  }

  // Get menu categories for a restaurant
  async getMenuCategories(restaurantId) {
    try {
      const items = await this.getMenuItems(restaurantId);
      const categories = [...new Set(items.map((item) => item.category))];
      return categories.filter((category) => category && category !== "");
    } catch (error) {
      console.error("Error getting menu categories:", error);
      throw error;
    }
  }

  /**
   * PHASE 2 COMPATIBILITY METHODS
   * Enhanced methods for AI-powered menu management
   */

  /**
   * Search menu items with natural language processing
   */
  async searchMenuItems(restaurantId, query, options = {}) {
    try {
      // Basic text search for now - can be enhanced with AI later
      const allItems = await this.getMenuItems(restaurantId);

      const searchTerm = query.toLowerCase();
      const results = allItems.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchTerm) ||
          item.description?.toLowerCase().includes(searchTerm) ||
          item.category?.toLowerCase().includes(searchTerm) ||
          item.varietal?.toLowerCase().includes(searchTerm) ||
          item.tags?.some((tag) => tag.toLowerCase().includes(searchTerm))
      );

      return {
        query,
        results,
        total: results.length,
        enhanced: false, // Indicates basic search, not AI-enhanced
      };
    } catch (error) {
      console.error("Error searching menu items:", error);
      throw error;
    }
  }

  /**
   * Get menu items with AI enhancement data
   */
  async getEnhancedMenuItems(restaurantId, options = {}) {
    try {
      const items = await this.getMenuItems(restaurantId);

      // Add enhancement status to each item
      return items.map((item) => ({
        ...item,
        aiEnhanced: !!item.aiEnhanced,
        lastEnhanced: item.lastEnhanced || null,
        enhancementScore: item.enhancementScore || 0,
      }));
    } catch (error) {
      console.error("Error getting enhanced menu items:", error);
      throw error;
    }
  }

  /**
   * Update menu item with AI enhancement data
   */
  async updateMenuItemWithEnhancement(restaurantId, itemId, enhancementData) {
    try {
      const updateData = {
        ...enhancementData,
        lastEnhanced: new Date().toISOString(),
        aiEnhanced: true,
        updatedAt: new Date().toISOString(),
      };

      await this.updateMenuItem(restaurantId, itemId, updateData);

      return {
        success: true,
        itemId,
        enhanced: true,
        timestamp: updateData.lastEnhanced,
      };
    } catch (error) {
      console.error("Error updating menu item with enhancement:", error);
      throw error;
    }
  }

  /**
   * Get menu statistics for AI dashboard
   */
  async getMenuStatistics(restaurantId) {
    try {
      const items = await this.getMenuItems(restaurantId);

      const stats = {
        total: items.length,
        byCategory: {},
        enhanced: 0,
        needsEnhancement: 0,
        averagePrice: 0,
        priceRange: { min: Infinity, max: 0 },
      };

      items.forEach((item) => {
        // Category breakdown
        const category = item.category || "uncategorized";
        stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;

        // Enhancement tracking
        if (item.aiEnhanced) {
          stats.enhanced++;
        } else {
          stats.needsEnhancement++;
        }

        // Price analysis
        if (item.price && typeof item.price === "number") {
          stats.averagePrice += item.price;
          stats.priceRange.min = Math.min(stats.priceRange.min, item.price);
          stats.priceRange.max = Math.max(stats.priceRange.max, item.price);
        }
      });

      stats.averagePrice =
        items.length > 0 ? stats.averagePrice / items.length : 0;
      stats.priceRange.min =
        stats.priceRange.min === Infinity ? 0 : stats.priceRange.min;

      return stats;
    } catch (error) {
      console.error("Error getting menu statistics:", error);
      throw error;
    }
  }

  /**
   * Batch update multiple menu items
   */
  async batchUpdateMenuItems(restaurantId, updates) {
    try {
      const results = [];

      for (const update of updates) {
        try {
          await this.updateMenuItem(restaurantId, update.id, update.data);
          results.push({ id: update.id, success: true });
        } catch (error) {
          results.push({
            id: update.id,
            success: false,
            error: error.message,
          });
        }
      }

      return {
        total: updates.length,
        successful: results.filter((r) => r.success).length,
        failed: results.filter((r) => !r.success).length,
        results,
      };
    } catch (error) {
      console.error("Error in batch update:", error);
      throw error;
    }
  }

  // Migrate existing data to include restaurant ID
  async migrateExistingData(restaurantId) {
    try {
      // Get all existing items from the old 'drinks' collection
      const allItemsQuery = query(collection(db, "drinks"));

      const snapshot = await getDocs(allItemsQuery);

      const migrationResults = [];

      for (const docSnap of snapshot.docs) {
        const itemData = docSnap.data();

        // Add to new collection with restaurantId
        const newItem = await this.addMenuItem(restaurantId, {
          ...itemData,
          source: "migration",
        });

        migrationResults.push(newItem);
      }

      return {
        migrated: migrationResults.length,
        items: migrationResults,
      };
    } catch (error) {
      console.error("Error migrating existing data:", error);
      throw error;
    }
  }
}

// Create singleton instance
export const menuService = new MenuService();
